/// <amd-module name="VSSUI/Components/EditableDropdown/CustomEditableDropdown" />
import * as React from "react";
import { ObservableArray, ObservableLike, ObservableValue } from "VSS/Core/Observable";
import { TimerManagement } from "VSS/Core/TimerManagement";
import { equals, startsWith } from "VSS/Core/Util/String";
import { Dropdown, DropdownCallout, DropdownExpandableTextField, filterItems, IDropdownCalloutProps, IDropdownExpandableProps } from "VSSUI/Dropdown";
import { IExpandable } from "VSSUI/Expandable";
import { IListSelection } from "VSSUI/List";
import { IListBoxItem, isListBoxItemVisible, ListBox, ListBoxItemType, renderListBoxCell } from "VSSUI/ListBox";
import { Observer } from "VSSUI/Observer";
import * as Resources from "VSSUI/Resources.Dropdown";
import { ITableColumn } from "VSSUI/Table";
import { ITreeColumn } from "VSSUI/TreeEx";
import { css, KeyCode } from "VSSUI/Util";
import { DropdownSelection } from "VSSUI/Utilities/DropdownSelection";
import { EditableDropdownItemProvider } from "VSSUI/Utilities/EditableDropdownItemProvider";
import { IFocusable } from "VSSUI/Utilities/Focus";
import { ISelectionRange } from "VSSUI/Utilities/Selection";
import { ITreeItemEx } from "VSSUI/Utilities/TreeItemProvider";
import { filterTreeItems, renderHighlightedText } from "../Dropdown/Dropdown";
import { IFilterResults } from "../Dropdown/Dropdown.Props";
import { IEditableDropdownProps } from "./EditableDropdown.Props";

export class CustomEditableDropdown<T = {}> extends React.Component<IEditableDropdownProps<T>> implements IExpandable, IFocusable<{}> {
    public static defaultProps = {
        allowClear: true,
        autoAccept: true,
        renderExpandable: DropdownExpandableTextField,
        renderCallout: DropdownCallout,
        renderItem: renderListBoxCell
    };

    private columns: ITreeColumn<IListBoxItem<T>>[];
    private dropdown = React.createRef<Dropdown>();
    private filteredItems: ObservableArray<IListBoxItem<T>>;
    private selection: IListSelection;
    private itemProvider: EditableDropdownItemProvider<T>;
    private isExpanded: boolean;
    private selectedItemInList: boolean;
    private selectedFreeform: boolean;
    private listBox = React.createRef<ListBox<T>>();
    private filteredIndexMap = new ObservableValue<number[]>([]);
    private filterMatches: number[][] = [];
    private previousFocusedIndex: number;
    private focusedIndex: ObservableValue<number>;
    private lastSelectedItem?: IListBoxItem<T>;

    private timerManagement: TimerManagement;

    public constructor(props: IEditableDropdownProps<T>) {
        super(props);

        this.selection = props.selection || new DropdownSelection();
        this.itemProvider = new EditableDropdownItemProvider<T>(props.items, this.selection);
        this.filteredItems = new ObservableArray<IListBoxItem<T>>([...this.itemProvider.value]);
        this.focusedIndex = new ObservableValue<number>(-1);
        this.previousFocusedIndex = -1;
        this.timerManagement = new TimerManagement();
        this.filteredIndexMap.value = this.itemProvider.value.map((item, index) => index);
        this.selectSelectedTextItem();
        if ($DEBUG && this.selection.multiSelect) {
            console.warn("multiselect selection is being used, EditableDropdown does not support multiselect");
        }
        if (this.props.columns) {
            // copy columns and wrap the render function of each column to get focus treatment
            this.columns = this.props.columns.map(col => {
                return {
                    ...col,
                    renderCell: (rowIndex, columnIndex, treeColumn, treeItem) =>
                        this.wrapWithFocusedIndexObserver(rowIndex, columnIndex, treeColumn, treeItem, col.renderCell)
                } as ITreeColumn<IListBoxItem<T>>;
            });
        }
    }

    public render() {
        const {
            actions,
            allowTextSelection,
            ariaLabel,
            ariaLabelledBy,
            autoSelect,
            calloutContentClassName,
            className,
            disabled,
            filterByText,
            getUnselectableRanges,
            inputId,
            noItemsText,
            onToggle,
            selectedText,
            showTree,
            text,
            minCalloutWidth,
            required,
        } = this.props;
        return (
            <Observer
                text={text}
                items={{ observableValue: this.itemProvider, filter: this.onItemsChange }}
                selection={this.selection}
                selectedText={selectedText}
            >
                {(props: { selectedText: string | undefined; selection: ISelectionRange[] }) => {
                    let placeholder = this.props.placeholder;
                    if (!allowTextSelection) {
                        if (props.selectedText) {
                            placeholder = props.selectedText;
                        } else if (props.selection.length) {
                            const selectedIndex = props.selection[0].beginIndex;
                            if (selectedIndex > -1) {
                                placeholder = this.itemProvider.value[selectedIndex].text;
                            }
                        }
                    }
                    return (
                        <Dropdown
                            ariaLabelledBy={ariaLabelledBy}
                            actions={actions}
                            ariaLabel={ariaLabel}
                            autoSelect={autoSelect ?? false}
                            calloutContentClassName={calloutContentClassName}
                            className={css(
                                "bolt-editable-dropdown",
                                (props.selection.length > 0 || !!props.selectedText) && "bolt-editable-dropdown-with-selection",
                                className
                            )}
                            columns={this.columns}
                            disabled={disabled}
                            getUnselectableRanges={getUnselectableRanges}
                            filterByText={filterByText}
                            inputId={inputId}
                            items={this.itemProvider}
                            noItemsText={noItemsText || Resources.NoItemsFound}
                            onCollapse={this.onCollapse}
                            onExpand={this.onExpand}
                            onSelect={this.onSelect}
                            onToggle={onToggle}
                            placeholder={placeholder}
                            ref={this.dropdown}
                            renderCallout={this.renderCallout}
                            renderExpandable={this.renderExpandable}
                            renderItem={this.renderItem}
                            selection={this.selection}
                            showFilterBox={false}
                            showTree={showTree}
                            userFilteredItems={this.filteredItems}
                            userFilteredItemsIndexMap={this.filteredIndexMap}
                            minCalloutWidth={minCalloutWidth}
                            required={required}
                        />
                    );
                }}
            </Observer>
        );
    }

    public componentDidMount(): void {
        if (this.props.filterThrottleWait) {
            this.filterItems = this.timerManagement.debounce(this.filterItems, this.props.filterThrottleWait);
        }
    }

    public collapse = (): void => {
        if (this.dropdown.current) {
            this.dropdown.current.collapse();
        }
    };

    public expand = (): void => {
        if (this.dropdown.current) {
            this.dropdown.current.expand();
        }
    };

    public focus() {
        if (this.dropdown.current) {
            this.dropdown.current.focus();
        }
    }

    private renderItem = (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<IListBoxItem<T>>, tableItem: IListBoxItem<T>) => {
        return this.wrapWithFocusedIndexObserver(rowIndex, columnIndex, tableColumn, tableItem, (rowIndex, columnIndex, tableColumn, tableItem) => {
            const item = tableItem as IListBoxItem<T>;
            const column = tableColumn as ITableColumn<IListBoxItem<T>>;
            const filterMatches = this.filterMatches[rowIndex];
            if (!item.render && filterMatches && filterMatches.length) {
                item.render = (rowIndex, columnIndex, tableColumn, tableItem) =>
                    renderHighlightedText(rowIndex, columnIndex, tableColumn, tableItem, filterMatches);
            }
            return this.props.renderItem!(rowIndex, columnIndex, column, item);
        });
    };

    private wrapWithFocusedIndexObserver = (
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<IListBoxItem<T>> | ITableColumn<ITreeItemEx<IListBoxItem<T>>>,
        tableItem: IListBoxItem<T> | ITreeItemEx<IListBoxItem<T>>,
        render: (
            rowIndex: number,
            columnIndex: number,
            tableColumn: ITableColumn<IListBoxItem<T>> | ITableColumn<ITreeItemEx<IListBoxItem<T>>>,
            tableItem: IListBoxItem<T> | ITreeItemEx<IListBoxItem<T>>
        ) => JSX.Element
    ) => {
        return (
            <Observer
                focusedIndex={{
                    observableValue: this.focusedIndex,
                    filter: () => {
                        const itemIndex = this.filteredIndexMap.value[rowIndex];
                        return itemIndex === this.focusedIndex.value || itemIndex === this.previousFocusedIndex;
                    }
                }}
                key={`focused-observer-${rowIndex}-${columnIndex}`}
            >
                {() => {
                    const item =
                        this.filteredIndexMap.value[rowIndex] === this.focusedIndex.value &&
                        (tableItem as IListBoxItem<T>)?.type !== ListBoxItemType.Loading
                            ? { ...tableItem, className: css(tableItem.className, "bolt-editable-dropdown-focused-item") }
                            : { ...tableItem };

                    return render(rowIndex, columnIndex, tableColumn, item);
                }}
            </Observer>
        );
    };

    private onCollapse = () => {
        const { allowFreeform, autoAccept, onCollapse, onValueChange, showTree, text } = this.props;

        if (onCollapse) {
            onCollapse();
        }

        if (!this.selectedItemInList) {
            const items = this.itemProvider.value;
            // If the text matches an item in the list, select that item.
            const index = items.findIndex(
                (item: IListBoxItem<T>, index: number) => this.selection.selectable(index) && item.text === ObservableLike.getValue(text || "")
            );
            if (index > -1) {
                this.selectIndex(index);
            }
        }

        const lastIndex = this.itemProvider.length - 1;
        // If collapsing with the freeform item in the list, if the last value is selected or no value is selected, select the value in the textField.
        if (
            allowFreeform &&
            (!this.selection.value.length || this.selection.value[0].beginIndex === lastIndex || !this.selectedItemInList) &&
            this.itemProvider.hasExtraItem
        ) {
            if (autoAccept || this.selectedItemInList || this.selectedFreeform) {
                const selectedText = this.itemProvider.value[lastIndex].id;
                if (onValueChange) {
                    onValueChange({ id: selectedText, text: selectedText });
                }
            }
            this.selectedFreeform = false;
            this.selection.clear();
        } else if (showTree && this.lastSelectedItem) {
            // tree items may be collapsed, so selection index isn't guaranteed to be accurate
            if (onValueChange) {
                onValueChange(this.lastSelectedItem);
            }
        } else if (this.selection.value.length) {
            const selectedItem = this.itemProvider.value[this.selection.value[0].beginIndex];
            if (onValueChange) {
                onValueChange(selectedItem);
            }
        }

        // Clear the filter text, showing the selected item.
        this.props.onTextChange?.(null, "");

        this.isExpanded = false;
        this.selectedItemInList = false;
        this.lastSelectedItem = undefined;
        if (showTree) {
            this.focusedIndex.value = -1;
        }
    };

    private onItemsChange = (value: IListBoxItem<T>[], action: string) => {
        if (!this.isExpanded) {
            return;
        }
        // Update the filtered set if items were added or removed.
        if (action !== "change") {
            this.filterItems();
        }

        this.selectSelectedTextItem();
    };

    private selectSelectedTextItem = () => {
        if (this.props.selectedText) {
            const selectedText = ObservableLike.getValue(this.props.selectedText);
            if (selectedText) {
                const selectedIndex = this.itemProvider.value.findIndex(item => item.text === selectedText);
                if (selectedIndex > -1) {
                    this.selection.select(selectedIndex);
                }
            }
        }
    };

    private onSelect = (event: React.SyntheticEvent<HTMLElement, Event>, item: IListBoxItem<T>) => {
        // Set to true when an explicit selection is made.  If false, it means a user is making a freeform selection by blurring.
        this.selectedItemInList = true;
        this.lastSelectedItem = item;
    };

    private renderExpandable = (props: IDropdownExpandableProps) => {
        return (
            <Observer focusedIndex={this.focusedIndex} selectedText={this.props.selectedText} text={this.props.text}>
                {(observerProps: { focusedIndex: number; selectedText: string | undefined; text: string | undefined }) => {
                    const { allowTextSelection, inputId } = this.props;
                    const { selectedText, text } = observerProps;
                    const focusedIndex = this.getFocusedIndex();
                    let activeId;
                    if (focusedIndex > -1 && this.itemProvider.value[focusedIndex]) {
                        activeId = this.itemProvider.value[focusedIndex].id;
                    }

                    const value = allowTextSelection && selectedText && !this.isExpanded ? selectedText : text;

                    const expandableProps = {
                        ...props,
                        ariaActiveDescendant: activeId,
                        editable: true,
                        showPrefix: text ? false : true,
                        blurDismiss: true,
                        inputId,
                        onChange: this.onTextChange,
                        onKeyDown: this.onKeyDown,
                        value
                    };
                    return this.props.renderExpandable?.(expandableProps);
                }}
            </Observer>
        );
    };

    private renderCallout = (props: IDropdownCalloutProps) => {
        const calloutProps = {
            ...props,
            focusOnMount: false,
            excludeTabStop: true,
            excludeFocusZone: true,
            ignoreMouseDown: true,
            listBoxRef: this.listBox
        };
        return this.props.renderCallout!(calloutProps);
    };

    private onExpand = () => {
        if (this.props.onExpand) {
            this.props.onExpand();
        }

        if (this.props.filterItems) {
            const filterResult = this.props.filterItems("", this.itemProvider.value);
            const selectedIndex = this.updateFilteredIndexMap(filterResult.filteredIndexMap);
            this.filteredItems.value = filterResult.filteredItems;
            this.focusItem(selectedIndex);
        } else {
            this.filteredItems.value = this.itemProvider.value;

            const focusedIndex = this.updateFilteredIndexMap(this.filteredItems.value.map((item, index) => index));
            this.focusItem(focusedIndex);
        }

        this.isExpanded = true;
    };

    private onTextChange = (
        event: React.SyntheticEvent<HTMLElement, Event> | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | null,
        text: string
    ) => {
        this.props.onTextChange?.(event, text);

        if (this.props.allowFreeform) {
            if (this.itemProvider.hasExtraItem) {
                // Remove the freeform item from the filtered set first so we don't
                // get a warning that the filtered set is greater than the item set.
                this.filteredItems.pop();
            }
            this.itemProvider.setTextValue(text);
            if (this.props.allowClear && text === "") {
                this.selection.clear();
                this.props.onValueChange?.();
            }
        } else if (text === "" && this.props.allowClear) {
            this.selection.clear();
            this.props.onValueChange?.();
        } else {
            const selectedIndex = this.itemProvider.value.findIndex(item => item.text === text);
            if (selectedIndex > -1 && this.selection.selectable(selectedIndex)) {
                this.selection.select(selectedIndex);
                this.props.onValueChange?.(this.itemProvider.value[selectedIndex]);
            }
        }
        if (this.isExpanded) {
            this.filterItems();
        }

        return false;
    };

    private filterItems = () => {
        const items = this.itemProvider.value;
        const text = ObservableLike.getValue(this.props.text || "");
        let filterResult: IFilterResults<T>;
        let firstMatchIndex: number | undefined;
        if (this.props.filterItems) {
            filterResult = this.props.filterItems(text, items);
        } else if (text) {
            if (this.props.showTree) {
                const [result, firstIndex] = filterTreeItems<T>(items, text, [], this.props.filterItem, this.props.filterMatchedItem);
                filterResult = result;
                firstMatchIndex = firstIndex;
            } else {
                filterResult = filterItems<T>(items, text, [], this.props.filterItem);

                // focus the first full or partial match in the list of filter results
                firstMatchIndex = items.findIndex(item => equals(item.text ?? "", text, true));
                if (firstMatchIndex < 0) {
                    firstMatchIndex = items.findIndex(item => startsWith(item.text ?? "", text, true));
                }
            }
        } else {
            filterResult = {
                filteredItems: items,
                filteredIndexMap: items.map((item, index) => index),
                filterMatches: []
            };
        }

        this.filterMatches = filterResult.filterMatches;
        const selectedIndex = this.updateFilteredIndexMap(filterResult.filteredIndexMap);
        this.filteredItems.value = filterResult.filteredItems;

        // if a tree is being filtered, we want to focus the first actual match in the list (as opposed to ancestor of that match)
        const indexToSelect = firstMatchIndex && firstMatchIndex > -1 ? firstMatchIndex : selectedIndex;
        this.focusItem(indexToSelect);
    };

    private onKeyDown = (ev: React.KeyboardEvent<HTMLElement>): void => {
        const keyCode = ev.which;
        const initiallyExpanded = this.isExpanded;
        switch (keyCode) {
            case KeyCode.escape:
                if (this.isExpanded) {
                    this.collapse();
                    ev.preventDefault();
                }
                break;

            case KeyCode.enter:
                if (!this.isExpanded) {
                    this.expand();
                    ev.preventDefault();
                }
            case KeyCode.tab:
                if (initiallyExpanded && !ev.shiftKey && (this.filteredItems.length || this.props.allowFreeform)) {
                    const focusedIndex = this.getFocusedIndex();
                    if (focusedIndex >= 0) {
                        this.selectIndex(focusedIndex);
                    } else {
                        this.selectedFreeform = true;
                    }
                    this.collapse();
                    ev.preventDefault();
                }
                break;

            case KeyCode.upArrow:
                if (this.isExpanded) {
                    this.focusPreviousItem();
                    if (this.listBox.current) {
                        this.listBox.current.scrollIntoView(this.filteredIndexMap.value.indexOf(this.focusedIndex.value), {
                            block: "nearest"
                        });
                    }
                }
                ev.preventDefault();
                break;

            case KeyCode.rightArrow:
                if (this.isExpanded && this.props.showTree) {
                    const focusedIndex = this.getFocusedIndex();
                    const item = this.itemProvider.value[focusedIndex];
                    if (!item.expanded) {
                        this.props.onToggle && this.props.onToggle(ev, item);
                        /** Kinda hacky but this is the only way to get the items to properly update */
                        this.focusNextItem();
                        this.focusPreviousItem();
                        if (this.listBox.current) {
                            this.listBox.current.scrollIntoView(this.filteredIndexMap.value.indexOf(focusedIndex), {
                                block: "nearest"
                            });
                        }
                    }
                }
                break;

            case KeyCode.downArrow:
                if (this.isExpanded) {
                    this.focusNextItem();
                    if (this.listBox.current) {
                        this.listBox.current.scrollIntoView(this.filteredIndexMap.value.indexOf(this.focusedIndex.value), {
                            block: "nearest"
                        });
                    }
                } else if (!this.isExpanded) {
                    this.expand();
                }
                ev.preventDefault();
                break;

            case KeyCode.leftArrow:
                if (this.isExpanded && this.props.showTree) {
                    const focusedIndex = this.getFocusedIndex();
                    const item = this.itemProvider.value[focusedIndex];
                    if (item.expanded) {
                        this.props.onToggle && this.props.onToggle(ev, item);
                        /** Kinda hacky but this is the only way to get the items to properly update */
                        this.focusPreviousItem();
                        this.focusNextItem();
                        if (this.listBox.current) {
                            this.listBox.current.scrollIntoView(this.filteredIndexMap.value.indexOf(focusedIndex), {
                                block: "nearest"
                            });
                        }
                    }
                }
                break;

            case KeyCode.delete:
            case KeyCode.backspace:
                if (this.props.allowClear && !ObservableLike.getValue(this.props.text || "")) {
                    this.selection.clear();
                    if (this.props.onValueChange) {
                        this.props.onValueChange();
                    }
                } else {
                    this.expand();
                }
                break;

            case KeyCode.ctrl:
            case KeyCode.shift:
                // The Ctrl key is used for most screen readers to stop speech.
                // while Shift is used for keyboard navigation
                // Ignore Shift and Ctrl key down.
                break;

            default:
                this.expand();
        }
    };

    private selectIndex(index: number) {
        if (index > -1) {
            this.selection.select(index);
            this.selectedItemInList = true;
        }
    }

    private focusItem(index?: number) {
        if (index !== undefined && index > -1 && this.isFocusable(index)) {
            this.previousFocusedIndex = this.focusedIndex.value;
            this.focusedIndex.value = index;
        }
    }

    private updateFilteredIndexMap(filteredIndexMap: number[]): number | undefined {
        // Try to maintain the focused index relative to what's being filtered.
        const prevFilteredFocusedIndex = this.filteredIndexMap.value.indexOf(this.focusedIndex.value);
        this.filteredIndexMap.value = filteredIndexMap;
        let focusedIndex: number | undefined = filteredIndexMap[prevFilteredFocusedIndex];
        if (
            !focusedIndex ||
            focusedIndex < 0 ||
            !this.selection.selectable(focusedIndex) ||
            this.filteredIndexMap.value.indexOf(focusedIndex) === -1
        ) {
            // If unable to maintain the focused index, focus the first selectable item.
            focusedIndex = !this.props.showTree ? this.filteredIndexMap.value.find(item => this.selection.selectable(item)) : -1;
        }
        return focusedIndex;
    }

    private focusNextItem(): void {
        let nextIndex;
        const filteredFocusedIndex = this.filteredIndexMap.value.indexOf(this.focusedIndex.value);
        for (let i = filteredFocusedIndex + 1; i < this.filteredIndexMap.value.length; i++) {
            if (this.selection.selectable(this.filteredIndexMap.value[i])) {
                nextIndex = this.filteredIndexMap.value[i];
                break;
            }
        }
        this.focusItem(nextIndex);
    }

    private focusPreviousItem(): void {
        let prevIndex;
        const filteredFocusedIndex = this.filteredIndexMap.value.indexOf(this.focusedIndex.value);
        for (let i = filteredFocusedIndex - 1; i >= 0; i--) {
            if (this.selection.selectable(this.filteredIndexMap.value[i])) {
                prevIndex = this.filteredIndexMap.value[i];
                break;
            }
        }
        this.focusItem(prevIndex);
    }

    private getFocusedIndex(): number {
        if (!this.props.showTree) {
            return this.focusedIndex.value;
        }

        // get the correct focusedIndex by increasing the current index by the number of collapsed rows that come before it
        let currentFocusedIndex = this.focusedIndex.value;
        for (let i = 0; i <= Math.min(currentFocusedIndex, this.itemProvider.value.length - 1); i++) {
            if (!isListBoxItemVisible(this.itemProvider.value[i])) {
                currentFocusedIndex++;
            }
        }

        return currentFocusedIndex;
    }

    private isFocusable(index: number): boolean {
        if (!this.props.showTree) {
            return true;
        }

        const visibleItems = this.itemProvider.value.filter(item => isListBoxItemVisible(item));
        return index < visibleItems.length;
    }
}
